import React, { useState } from "react";
import { setCompetitionMatchResult, updateCompetitionMatch } from "../../../api";
import { MATCH_STATUS, Match, MatchStatus, TournamentParticipant } from "../../../types";
import { toApiDateTimeValue, toDateTimeLocalValue } from "../../../utils/datetime";
import { AnchoredModal } from "../AnchoredModal";
import { Button } from "../Button";
import { SelectField, TextField } from "../form/FormField";

interface MatchEditModalProps {
  match: Match;
  participants: TournamentParticipant[];
  getSlotLabel: (match: Match, slot: 1 | 2) => string;
  enableCalendar: boolean;
  tournamentName?: string;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

export const MatchEditModal: React.FC<MatchEditModalProps> = ({
  match,
  participants,
  getSlotLabel,
  enableCalendar,
  tournamentName,
  onClose,
  onSaved,
}) => {
  const [player1, setPlayer1] = useState<number | null>(match.player1 ?? null);
  const [player2, setPlayer2] = useState<number | null>(match.player2 ?? null);
  const [score1, setScore1] = useState(match.score1 != null ? String(match.score1) : "");
  const [score2, setScore2] = useState(match.score2 != null ? String(match.score2) : "");
  const [scheduledAt, setScheduledAt] = useState(toDateTimeLocalValue(match.scheduled_at));
  const [durationMinutes, setDurationMinutes] = useState(String(match.duration_minutes || 60));
  const [status, setStatus] = useState<MatchStatus>(match.status);
  const [isSaving, setIsSaving] = useState(false);

  const player1Name = participants.find((participant) => participant.id === player1)?.name;
  const player2Name = participants.find((participant) => participant.id === player2)?.name;

  const updateScore = (setter: React.Dispatch<React.SetStateAction<string>>, value: string) => {
    if (value === "" || /^\d+$/.test(value)) {
      setter(value);
    }
  };

  const handleSave = async () => {
    const scheduled_at = enableCalendar ? toApiDateTimeValue(scheduledAt) : undefined;
    const hasScore1 = score1 !== "";
    const hasScore2 = score2 !== "";
    // Il punteggio guida lo stato: se è presente il match è "giocato".
    const hasResult = hasScore1 && hasScore2;

    if (player1 != null && player2 != null && player1 === player2) {
      alert("I partecipanti devono essere diversi.");
      return;
    }

    if (hasScore1 !== hasScore2) {
      alert("Inserisci entrambi i punteggi oppure lascia vuoti entrambi.");
      return;
    }

    if ((hasScore1 || hasScore2) && (player1 == null || player2 == null)) {
      alert("Seleziona entrambi i partecipanti prima di salvare il risultato.");
      return;
    }
    if (!hasResult && status === "ongoing" && (player1 == null || player2 == null)) {
      alert("Seleziona entrambi i partecipanti prima di disputare il match.");
      return;
    }
    const parsedDuration = Number(durationMinutes);
    if (enableCalendar && (!Number.isInteger(parsedDuration) || parsedDuration < 1)) {
      alert("Inserisci una durata valida in minuti.");
      return;
    }

    try {
      setIsSaving(true);
      await updateCompetitionMatch(match.id, {
        player1,
        player2,
        // Con un punteggio lo stato "played" (e il vincitore) lo imposta
        // setCompetitionMatchResult; altrimenti salviamo lo stato scelto azzerando
        // punteggio e vincitore.
        ...(hasResult ? {} : { status, score1: null, score2: null, winner: null }),
        ...(enableCalendar ? { scheduled_at, duration_minutes: parsedDuration } : {}),
      });
      if (hasResult) {
        await setCompetitionMatchResult(match.id, Number(score1), Number(score2));
      }
      await onSaved();
      onClose();
    } catch (error) {
      console.error(error);
      alert("Errore nel salvataggio del match: " + error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AnchoredModal
      title={`Match #${match.order ?? match.id}${tournamentName ? ` - ${tournamentName}` : ""}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Annulla
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            Salva
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <SelectField
          label="Giocatore 1"
          value={player1 ?? ""}
          onChange={(event) => setPlayer1(event.target.value === "" ? null : Number(event.target.value))}
        >
          <option value="">{getSlotLabel(match, 1)}</option>
          {participants.map((participant) => (
            <option key={participant.id} value={participant.id} disabled={player2 === participant.id}>
              {participant.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="Giocatore 2"
          value={player2 ?? ""}
          onChange={(event) => setPlayer2(event.target.value === "" ? null : Number(event.target.value))}
        >
          <option value="">{getSlotLabel(match, 2)}</option>
          {participants.map((participant) => (
            <option key={participant.id} value={participant.id} disabled={player1 === participant.id}>
              {participant.name}
            </option>
          ))}
        </SelectField>
        {enableCalendar && (
          <label className="block">
            Data e ora
            <div className="flex gap-2">
              <input
                type="datetime-local"
                className="min-w-0 flex-1 border border-gray-300 px-2 py-1 rounded"
                value={scheduledAt}
                onChange={(event) => setScheduledAt(event.target.value)}
              />
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => setScheduledAt("")}
                disabled={!scheduledAt}
              >
                Cancella
              </Button>
            </div>
          </label>
        )}
        <div className={`grid gap-3 ${enableCalendar ? "grid-cols-2" : "grid-cols-1"}`}>
          {enableCalendar && (
            <TextField
              label="Durata min"
              type="number"
              min={1}
              value={durationMinutes}
              onChange={(event) => setDurationMinutes(event.target.value)}
            />
          )}
          <SelectField
            label="Stato"
            value={status}
            onChange={(event) => setStatus(event.target.value as MatchStatus)}
          >
            {(Object.keys(MATCH_STATUS) as MatchStatus[]).map((value) => (
              <option key={value} value={value}>
                {MATCH_STATUS[value]}
              </option>
            ))}
          </SelectField>
        </div>
        <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-end">
          <h3 className="col-span-3 font-bold">Punteggio</h3>
          <p className="col-span-3 text-xs text-gray-500">
            Inserendo il punteggio il match verrà salvato come "{MATCH_STATUS.played}".
          </p>
          <TextField
            label={player1Name ? player1Name : "Punteggio 1"}
            type="text"
            value={score1}
            onChange={(event) => updateScore(setScore1, event.target.value)}
          />
          <span className="pb-2">-</span>
          <TextField
            label={player2Name ? player2Name : "Punteggio 2"}
            type="text"
            value={score2}
            onChange={(event) => updateScore(setScore2, event.target.value)}
          />
        </div>
      </div>
    </AnchoredModal>
  );
};
